import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { FileBoxIcon } from "lucide-react";
import { FieldErrors, useFormContext, useWatch } from "react-hook-form";
import { UnitTextField } from "@/common/components/UnitTextField";
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";
import { ProductCategoryDistributionLimitControlledByCurrencyValidationSchema, ProductCategoryDistributionLimitControlledByQuantityValidationSchema, ProductCategoryDistributionLimitValidationSchema, ProductDistributionLimitValidationSchema } from "../../schemas/distributionsLimits.schema";
import { unities, unitiesDecimals } from "@/common/constants/unities.constants";

interface ProductDistributionLimitFormItemProps {
  index: number;
  productDistributionLimit: ProductDistributionLimitValidationSchema;
  onDelete: () => void;
  onTotalValueChange: () => void;
  controlledBy?: CompanyContractConsumptionControlledBy;
}

const ProductDistributionLimitFormItem = ({
  index,
  productDistributionLimit,
  onDelete,
  onTotalValueChange,
  controlledBy = CompanyContractConsumptionControlledBy.Currency,
}: ProductDistributionLimitFormItemProps) => {
  const isQuantity = controlledBy === 'quantity';

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<ProductCategoryDistributionLimitValidationSchema>();

  const currentValue = useWatch({
    control,
    name: (isQuantity
      ? `product_distribution_limits_attributes.${index}.maximum_quantity`
      : `product_distribution_limits_attributes.${index}.maximum_value`),
  });

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={FileBoxIcon}
        iconProps={{ className: 'size-6', strokeWidth: 1 }}
        text={productDistributionLimit?.product?.display_name}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 pe-2 gap-0"
        overwriteClassName
        contentClassName="px-3 py-2"
        onDelete={onDelete}
      />

      <div className='flex flex-col gap-1'>
        {isQuantity ? (
          <UnitTextField
            label="Quantidade limite"
            unit={productDistributionLimit.product.unit_for_quantity
              ? unities[productDistributionLimit.product.unit_for_quantity]
              : undefined}
            decimals={productDistributionLimit.product.unit_for_quantity
              ? unitiesDecimals[productDistributionLimit.product.unit_for_quantity]
              : 2}
            autoCorrect="off"
            {...register(`product_distribution_limits_attributes.${index}.maximum_quantity`)}
            forceValue
            value={currentValue}
            onChange={(event) => {
              register(`product_distribution_limits_attributes.${index}.maximum_quantity`).onChange(event);
              onTotalValueChange();
            }}
            errorMessage={
              (errors as FieldErrors<ProductCategoryDistributionLimitControlledByQuantityValidationSchema>)
                .product_distribution_limits_attributes
                ?.[index]
                ?.maximum_quantity
                ?.message || undefined
            }
          />
        ) : (
          <CurrencyTextField
            label="Valor limite"
            {...register(`product_distribution_limits_attributes.${index}.maximum_value`)}
            onChange={(event) => {
              register(`product_distribution_limits_attributes.${index}.maximum_value`).onChange(event);
              onTotalValueChange();
            }}
            forceValue
            value={currentValue}
            errorMessage={
              (errors as FieldErrors<ProductCategoryDistributionLimitControlledByCurrencyValidationSchema>)
                .product_distribution_limits_attributes
                ?.[index]
                ?.maximum_value
                ?.message || undefined
            }
          />
        )}
      </div>
    </Box>
  );
};

export default ProductDistributionLimitFormItem;
